import { useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { TimeSlotPicker } from './TimeSlotPicker';
import { Trash2, RotateCcw, ChevronDown, CalendarDays } from 'lucide-react';

// JS weekday index: 0=Sun,1=Mon,...,6=Sat → map to our keys
const WEEKDAY_JS_MAP = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };

/** Calculate next N matching dates for an entry */
const getNextMatchingDates = (entry, count = 6) => {
  if (!entry.weekday) return [];
  const targetDay = WEEKDAY_JS_MAP[entry.weekday];
  if (targetDay === undefined) return [];
  const pattern = entry.pattern || 'weekly';
  const dates = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (pattern === 'monthly') {
    const mw = entry.monthly_week || 'first';
    // Scan next 12 months
    for (let m = 0; m < 12 && dates.length < count; m++) {
      const year = today.getFullYear() + Math.floor((today.getMonth() + m) / 12);
      const month = (today.getMonth() + m) % 12;
      let found = null;
      if (mw === 'last') {
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        for (let d = daysInMonth; d >= 1; d--) {
          const dt = new Date(year, month, d);
          if (dt.getDay() === targetDay) { found = dt; break; }
        }
      } else {
        const weekMap = { first: 1, second: 2, third: 3, fourth: 4 };
        const n = weekMap[mw] || 1;
        let occ = 0;
        for (let d = 1; d <= 31; d++) {
          const dt = new Date(year, month, d);
          if (dt.getMonth() !== month) break;
          if (dt.getDay() === targetDay) {
            occ++;
            if (occ === n) { found = dt; break; }
          }
        }
      }
      if (found && found >= today) dates.push(found);
    }
  } else {
    // Weekly
    const freq = entry.frequency || 1;
    // Find next occurrence of the target weekday
    let cursor = new Date(today);
    const diff = (targetDay - cursor.getDay() + 7) % 7;
    cursor.setDate(cursor.getDate() + (diff === 0 ? 0 : diff));

    if (freq > 1 && entry.start_date) {
      const start = new Date(entry.start_date);
      start.setHours(0, 0, 0, 0);
      // Align cursor to the correct week based on start_date
      while (cursor >= today && dates.length < count) {
        const daysDiff = Math.floor((cursor - start) / (1000 * 60 * 60 * 24));
        const weeksDiff = Math.floor(daysDiff / 7);
        if (daysDiff >= 0 && weeksDiff % freq === 0) {
          dates.push(new Date(cursor));
        }
        cursor.setDate(cursor.getDate() + 7);
        if (dates.length === 0 && cursor - today > 7 * freq * 1000 * 60 * 60 * 24) break;
      }
    } else {
      for (let i = 0; i < count; i++) {
        if (cursor >= today) dates.push(new Date(cursor));
        cursor.setDate(cursor.getDate() + 7 * freq);
      }
    }
  }
  return dates.slice(0, count);
};

const WEEKDAY_KEYS = [
  { value: '', labelKey: 'alternativeOpening.selectDay' },
  { value: 'monday', labelKey: 'calendar.monday' },
  { value: 'tuesday', labelKey: 'calendar.tuesday' },
  { value: 'wednesday', labelKey: 'calendar.wednesday' },
  { value: 'thursday', labelKey: 'calendar.thursday' },
  { value: 'friday', labelKey: 'calendar.friday' },
  { value: 'saturday', labelKey: 'calendar.saturday' },
  { value: 'sunday', labelKey: 'calendar.sunday' },
];

/** Normalize a time string: "08.00" → "08:00" */
const normalizeTimeStr = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str.trim().replace(/\.+$/, '').replace(/\./g, ':').replace('–', '-').replace('—', '-');
};

export const AlternativeOpeningEditor = ({ entries = [], onChange }) => {
  const { t } = useLanguage();
  const items = Array.isArray(entries) ? entries : [];

  const updateEntry = (index, field, value) => {
    const updated = items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    onChange(updated);
  };

  const removeEntry = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const addEntry = () => {
    const newEntry = {
      id: Date.now().toString(),
      description: '',
      weekday: '',
      pattern: 'weekly',
      frequency: 1,
      monthly_week: 'first',
      start_date: '',
      time_from: '',
      time_to: '',
    };
    onChange([...items, newEntry]);
  };

  return (
    <div className="space-y-3" data-testid="alternative-opening-editor">
      <div className="flex items-center gap-2">
        <RotateCcw className="w-4 h-4 text-blue-400" />
        <span className="text-white font-semibold text-sm">{t('alternativeOpening.title')}</span>
      </div>

      <div className="p-3 bg-blue-500/5 border border-blue-500/15 rounded-xl">
        <p className="text-white/60 text-xs leading-relaxed">
          {t('alternativeOpening.description')}
          <br /><span className="text-blue-400 font-medium">{t('alternativeOpening.descriptionHighlight')}</span>
        </p>
      </div>

      {items.map((entry, index) => (
        <EntryRow
          key={entry.id || index}
          entry={entry}
          onUpdate={(field, value) => updateEntry(index, field, value)}
          onRemove={() => removeEntry(index)}
        />
      ))}

      <button
        type="button"
        onClick={addEntry}
        className="w-full py-2.5 border border-dashed border-zinc-600 rounded-xl text-zinc-400 hover:text-blue-400 hover:border-blue-400/40 transition-colors text-sm"
        data-testid="alt-opening-add-btn"
      >
        + {t('alternativeOpening.addButton')}
      </button>

      {items.length > 0 && (
        <div className="p-2.5 bg-blue-500/10 border border-blue-500/20 rounded-xl">
          <p className="text-blue-400 text-xs text-center font-medium">
            {t('alternativeOpening.saveReminder')}
          </p>
        </div>
      )}
    </div>
  );
};

const EntryRow = ({ entry, onUpdate, onRemove }) => {
  const { t } = useLanguage();
  const pattern = entry.pattern || 'weekly';
  const needsStartDate = pattern === 'weekly' && (entry.frequency || 1) > 1;

  // Build frequency label for display
  const getFrequencyLabel = () => {
    if (pattern === 'monthly') {
      const weekLabel = t(`alternativeOpening.monthly${capitalize(entry.monthly_week || 'first')}`);
      const dayLabel = entry.weekday ? t(`calendar.${entry.weekday}`) : '...';
      return `${weekLabel} ${dayLabel} ${t('alternativeOpening.inMonth')}`;
    }
    const freq = entry.frequency || 1;
    if (freq === 1) return t('alternativeOpening.everyWeek');
    return t('alternativeOpening.everyNWeeks').replace('{n}', freq);
  };

  return (
    <div
      className="px-3 py-3 bg-zinc-800/40 border border-white/5 rounded-xl group space-y-2.5"
      data-testid={`alt-opening-${entry.id}`}
    >
      {/* Description */}
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <input
            type="text"
            value={entry.description}
            onChange={(e) => onUpdate('description', e.target.value)}
            placeholder={t('alternativeOpening.descriptionPlaceholder')}
            className="w-full bg-zinc-900/70 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm placeholder:text-zinc-600 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none transition-colors"
            data-testid="alt-opening-desc"
          />
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="p-2 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors opacity-60 group-hover:opacity-100"
          data-testid="alt-opening-remove"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Pattern selector: Weekly vs Monthly */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onUpdate('pattern', 'weekly')}
          className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
            pattern === 'weekly'
              ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
              : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:text-white'
          }`}
          data-testid="alt-pattern-weekly"
        >
          {t('alternativeOpening.patternWeekly')}
        </button>
        <button
          type="button"
          onClick={() => onUpdate('pattern', 'monthly')}
          className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
            pattern === 'monthly'
              ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
              : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:text-white'
          }`}
          data-testid="alt-pattern-monthly"
        >
          {t('alternativeOpening.patternMonthly')}
        </button>
      </div>

      {/* Weekday selector (always shown) */}
      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <select
            value={entry.weekday || ''}
            onChange={(e) => onUpdate('weekday', e.target.value)}
            className="w-full bg-zinc-900/70 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-xs focus:border-blue-400 outline-none transition-colors appearance-none"
            data-testid="alt-opening-weekday"
          >
            {WEEKDAY_KEYS.map(d => (
              <option key={d.value} value={d.value}>{t(d.labelKey)}</option>
            ))}
          </select>
          <ChevronDown className="w-3 h-3 text-zinc-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>

        {/* Weekly: frequency selector */}
        {pattern === 'weekly' && (
          <div className="relative flex-1">
            <select
              value={entry.frequency || 1}
              onChange={(e) => onUpdate('frequency', parseInt(e.target.value))}
              className="w-full bg-zinc-900/70 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-xs focus:border-blue-400 outline-none transition-colors appearance-none"
              data-testid="alt-opening-frequency"
            >
              {[1, 2, 3, 4].map(n => (
                <option key={n} value={n}>
                  {n === 1 ? t('alternativeOpening.everyWeek') : t('alternativeOpening.everyNWeeks').replace('{n}', n)}
                </option>
              ))}
            </select>
            <ChevronDown className="w-3 h-3 text-zinc-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        )}

        {/* Monthly: which week selector */}
        {pattern === 'monthly' && (
          <div className="relative flex-1">
            <select
              value={entry.monthly_week || 'first'}
              onChange={(e) => onUpdate('monthly_week', e.target.value)}
              className="w-full bg-zinc-900/70 border border-amber-500/30 rounded-lg px-3 py-1.5 text-white text-xs focus:border-amber-400 outline-none transition-colors appearance-none"
              data-testid="alt-opening-monthly-week"
            >
              {['first', 'second', 'third', 'fourth', 'last'].map(w => (
                <option key={w} value={w}>
                  {t(`alternativeOpening.monthly${capitalize(w)}`)}
                </option>
              ))}
            </select>
            <ChevronDown className="w-3 h-3 text-zinc-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        )}
      </div>

      {/* Summary label */}
      <div className="px-2 py-1 bg-zinc-900/50 rounded-lg">
        <p className="text-xs text-zinc-400 text-center italic">{getFrequencyLabel()}</p>
      </div>

      {/* Next dates preview */}
      <NextDatesPreview entry={entry} />

      {/* Start date (only for weekly freq > 1) */}
      {needsStartDate && (
        <div className="flex items-center gap-2">
          <CalendarDays className="w-3 h-3 text-amber-400 flex-shrink-0" />
          <span className="text-xs text-amber-400 flex-shrink-0">{t('alternativeOpening.firstTime')}</span>
          <input
            type="date"
            value={entry.start_date || ''}
            onChange={(e) => onUpdate('start_date', e.target.value)}
            className="flex-1 bg-zinc-900/70 border border-amber-500/30 rounded-lg px-3 py-1.5 text-white text-xs focus:border-amber-400 outline-none transition-colors"
            data-testid="alt-opening-start-date"
          />
          {!entry.start_date && (
            <span className="text-xs text-red-400">{t('alternativeOpening.required')}</span>
          )}
        </div>
      )}

      {/* Time from / to — dropdown pickers, no free text */}
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <TimeSlotPicker
            value={normalizeTimeStr(entry.time_from) || ''}
            onChange={(v) => onUpdate('time_from', v)}
            label={t('alternativeOpening.fromTime')}
            testId="alt-opening-from"
          />
        </div>
        <span className="text-zinc-500 text-xs pb-2">—</span>
        <div className="flex-1">
          <TimeSlotPicker
            value={normalizeTimeStr(entry.time_to) || ''}
            onChange={(v) => onUpdate('time_to', v)}
            allowOpenEnded={true}
            label={t('alternativeOpening.toTime')}
            testId="alt-opening-to"
          />
        </div>
      </div>
    </div>
  );
};

const NextDatesPreview = ({ entry }) => {
  const { t, language } = useLanguage();
  const dates = useMemo(() => getNextMatchingDates(entry, 6), [
    entry.weekday, entry.pattern, entry.frequency, entry.monthly_week, entry.start_date
  ]);

  if (!entry.weekday || dates.length === 0) return null;

  const locale = language === 'da' ? 'da-DK' : language === 'de' ? 'de-DE' : language === 'pl' ? 'pl-PL' : language === 'es' ? 'es-ES' : language === 'fr' ? 'fr-FR' : language === 'it' ? 'it-IT' : 'en-GB';

  return (
    <div className="px-2 py-1.5 bg-emerald-500/5 border border-emerald-500/15 rounded-lg" data-testid="alt-opening-preview">
      <p className="text-[10px] text-emerald-400/70 mb-1 font-medium">{t('alternativeOpening.nextDays')}:</p>
      <div className="flex flex-wrap gap-1">
        {dates.map((d, i) => (
          <span key={i} className="text-[10px] bg-emerald-500/10 text-emerald-400 px-1.5 py-0.5 rounded font-mono">
            {d.toLocaleDateString(locale, { day: 'numeric', month: 'short' })}
          </span>
        ))}
      </div>
    </div>
  );
};

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default AlternativeOpeningEditor;
